import { GenericResponse } from '@/types/downloadReports';

import { createAsyncAction } from 'typesafe-actions';

// ---- Types ----
export type ReportCenterRequestPayload = Record<string, any>;

export type ReportCenterSuccessPayload = {
  data: GenericResponse;
};
export interface CreateNewClassificationTagResponse {
  success: boolean;
  result: {
    success: boolean;
    data: {
      status_code: number;
      success: boolean;
      data: {
        classification_tag_id: number;
      };
      message: string;
    };
  };
}

// ---- Actions ----

export const getClusters = createAsyncAction(
  'reportCenter/getClusters/request',
  'reportCenter/getClusters/success',
  'reportCenter/getClusters/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const getClassificationTagsData = createAsyncAction(
  'reportCenter/getClassificationTagsData/request',
  'reportCenter/getClassificationTagsData/success',
  'reportCenter/getClassificationTagsData/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const createNewClassificationTag = createAsyncAction(
  'reportCenter/createNewClassificationTag/request',
  'reportCenter/createNewClassificationTag/success',
  'reportCenter/createNewClassificationTag/failure',
)<
  CreateNewClassificationTagPayload,
  CreateNewClassificationTagResponse,
  undefined
>();

export const getNewClusterData = createAsyncAction(
  'reportCenter/getNewClusterData/request',
  'reportCenter/getNewClusterData/success',
  'reportCenter/getNewClusterData/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const createClusterAndRelationship = createAsyncAction(
  'reportCenter/createClusterAndRelationship/request',
  'reportCenter/createClusterAndRelationship/success',
  'reportCenter/createClusterAndRelationship/failure',
)<
  void,
  CreateClusterAndRelationshipSuccess,
  CreateClusterAndRelationshipError
>();

export const getReportsForClusterData = createAsyncAction(
  'reportCenter/getReportsForClusterData/request',
  'reportCenter/getReportsForClusterData/success',
  'reportCenter/getReportsForClusterData/failure',
)<
  GetReportsForClusterDataPayload,
  GetReportsForClusterDataSuccessPayload,
  string
>();

export const createNewReportsForCluster = createAsyncAction(
  'reportCenter/createNewReportsForCluster/request',
  'reportCenter/createNewReportsForCluster/success',
  'reportCenter/createNewReportsForCluster/failure',
)<
  CreateNewReportsForClusterPayload,
  { clusterId: number },
  { error: string }
>();

export const createDashboardClassClusterReport = createAsyncAction(
  'reportCenter/createDashboardClassClusterReport/request',
  'reportCenter/createDashboardClassClusterReport/success',
  'reportCenter/createDashboardClassClusterReport/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const updateRegularClusterReport = createAsyncAction(
  'reportCenter/updateRegularClusterReport/request',
  'reportCenter/updateRegularClusterReport/success',
  'reportCenter/updateRegularClusterReport/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const updateSponsorDashReport = createAsyncAction(
  'reportCenter/updateSponsorDashReport/request',
  'reportCenter/updateSponsorDashReport/success',
  'reportCenter/updateSponsorDashReport/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const deleteClusterFrequencyReport = createAsyncAction(
  'reportCenter/deleteClusterFrequencyReport/request',
  'reportCenter/deleteClusterFrequencyReport/success',
  'reportCenter/deleteClusterFrequencyReport/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const getClassTagsTableData = createAsyncAction(
  'reportCenter/getClassTagsTableData/request',
  'reportCenter/getClassTagsTableData/success',
  'reportCenter/getClassTagsTableData/failure',
)<undefined, ReportCenterSuccessPayload, undefined>();

export const updateClassTagData = createAsyncAction(
  'reportCenter/updateClassTagData/request',
  'reportCenter/updateClassTagData/success',
  'reportCenter/updateClassTagData/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const getClustersTableData = createAsyncAction(
  'reportCenter/getClustersTableData/request',
  'reportCenter/getClustersTableData/success',
  'reportCenter/getClustersTableData/failure',
)<undefined, ReportCenterSuccessPayload, undefined>();

export const updateClustersTableData = createAsyncAction(
  'reportCenter/updateClustersTableData/request',
  'reportCenter/updateClustersTableData/success',
  'reportCenter/updateClustersTableData/failure',
)<ReportCenterRequestPayload, ReportCenterSuccessPayload, undefined>();

export const generateOnDemandReport = createAsyncAction(
  'reportCenter/generateOnDemandReport/request',
  'reportCenter/generateOnDemandReport/success',
  'reportCenter/generateOnDemandReport/failure',
)<OnDemandReportPayload, OnDemandReportAcknowledgement, undefined>();

export const getSavedReportsForClusterData = createAsyncAction(
  'reportCenter/getSavedReportsForClusterData/request',
  'reportCenter/getSavedReportsForClusterData/success',
  'reportCenter/getSavedReportsForClusterData/failure',
)<undefined, GetSavedReportsForClusterDataSuccessPayload, undefined>();

/* -------------------------------------------------------------------------- */
/*  Scheduled widgets (report_widget_* tables)                                */
/* -------------------------------------------------------------------------- */

export const getScheduledWidgetsReferenceData = createAsyncAction(
  'reportCenter/getScheduledWidgetsReferenceData/request',
  'reportCenter/getScheduledWidgetsReferenceData/success',
  'reportCenter/getScheduledWidgetsReferenceData/failure',
)<undefined, GetScheduledWidgetsReferenceDataSuccessPayload, undefined>();

export const getScheduledWidgets = createAsyncAction(
  'reportCenter/getScheduledWidgets/request',
  'reportCenter/getScheduledWidgets/success',
  'reportCenter/getScheduledWidgets/failure',
)<undefined, GetScheduledWidgetsSuccessPayload, undefined>();

export const createScheduledWidget = createAsyncAction(
  'reportCenter/createScheduledWidget/request',
  'reportCenter/createScheduledWidget/success',
  'reportCenter/createScheduledWidget/failure',
)<
  CreateScheduledWidgetPayload,
  CreateScheduledWidgetSuccessPayload,
  undefined
>();

export const updateScheduledWidget = createAsyncAction(
  'reportCenter/updateScheduledWidget/request',
  'reportCenter/updateScheduledWidget/success',
  'reportCenter/updateScheduledWidget/failure',
)<UpdateScheduledWidgetPayload, ScheduledWidgetIdPayload, undefined>();

export const deleteScheduledWidget = createAsyncAction(
  'reportCenter/deleteScheduledWidget/request',
  'reportCenter/deleteScheduledWidget/success',
  'reportCenter/deleteScheduledWidget/failure',
)<DeleteScheduledWidgetPayload, ScheduledWidgetIdPayload, undefined>();

export interface OnDemandReportPayload {
  report_cluster_frequency_id: number;
  is_monte_carlo?: boolean;
}

// Response after requesting a report (based on OnDemandAcknowledgementSerializer)
export interface OnDemandReportAcknowledgement {
  message: string; // e.g., "Acknowledgement email sent successfully"
  status?: string; // optional, depending on serializer fields
}

interface BaseClusterPayload {
  cluster_id?: string;
  user_id?: string;
}
export interface GetReportsForClusterDataMinimalPayload {
  user_id: string; // only required field
}

export interface GetReportsForClusterDataPayload {
  user_id: string;
}
export interface GetReportsForClusterDataSuccessPayload {
  clusters: Cluster[];
  frequencies: Frequency[];
  emails: string[];
  reports: Reporttype[];
  monte_carlo_report?: Reporttype | null;
}
export interface Cluster {
  id: number;
  cluster_name: string;
  is_sftp: number;
  is_email: number;
  calendar_pmca: {
    calendar_pmca_id: number;
    calendar_name: string;
    calendar: number;
  };
  classification_tags: {
    classification_tag_id: string;
    sponsor_id: number | null;
  }[];
}

export interface Frequency {
  id: number;
  report_name: string;
  description: string | null;
}

export interface Reporttype {
  report_id: number;
  report_code?: string | null;
  report_name: string;
  report_description: string | null;
  display_name: string;
}

interface BaseReportPayload {
  report_id?: string;
  user_id?: string;
}

// Cluster-related interfaces
export interface GetClustersPayload {
  filters?: {
    status?: string;
    date_range?: {
      start_date: string;
      end_date: string;
    };
    classification_tags?: string[];
  };
  pagination?: {
    page: number;
    page_size: number;
  };
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface GetNewClusterDataPayload {
  cluster_type?: string;
  template_id?: string;
  user_id: string;
}

export interface CreateClusterAndRelationshipPayload {
  user_id: string;
  data: {
    cluster_name: string;
    calendar_pmca_id?: number;
    classification_tag_ids: string[];
    is_email: boolean;
    is_sftp: boolean;
  };
}
export interface CreateClusterAndRelationshipSuccess {
  data: {
    cluster_id: number;
  };
}

export interface CreateClusterAndRelationshipError {
  message: string;
}
export interface UpdateClustersTableDataPayload extends BaseClusterPayload {
  cluster_name?: string;
  description?: string;
  status?: string;
  classification_tag_ids?: string[];
  metadata?: Record<string, any>;
}

// Classification Tag interfaces
export interface GetClassificationTagsDataPayload {
  filters?: {
    active_only?: boolean;
    tag_type?: string;
    search_term?: string;
  };
  pagination?: {
    page: number;
    page_size: number;
  };
}

export interface CreateNewClassificationTagPayload {
  user_id: string;
  data: {
    class_tag_name: string;
    comp_entity_id: string;
    base_currency_id: number;
    report_currency_id: number;
    fund_company_id: string | null;
    classification_currency_id: number;
    fund_mifid_inclusion_flag: boolean;
    executing_lei: string;
    trade_tag_id: number;
    company_id?: string | null;
  };
}

export interface UpdateClassTagDataPayload {
  user_id: string;
  data: {
    class_tag_id: number;
    comp_entity_id: string;
    trade_tag_id: number;
    base_currency_id: number;
    report_currency_id: number;
    classification_currency_id: number;
    fund_company_name: string;
    fund_company_id: string | null;
    class_tag_name?: string;
    mifid_executing_lei?: string;
    fund_mifid_inclusion_flag?: boolean;

    classification_flag?: boolean;
    fund_company_percentage_flag?: boolean;
    company_id?: string | null;
  };
}

// Reports-related interfaces
export interface GetReportsForClusterDataPayload extends BaseClusterPayload {
  filters?: {
    report_type?: string;
    status?: string;
    date_range?: {
      start_date: string;
      end_date: string;
    };
  };
  pagination?: {
    page: number;
    page_size: number;
  };
}

export interface CreateNewReportsForClusterPayload {
  user_id: string;
  data: {
    clusterId: number;
    frequency: number;
    isEmail: boolean;
    isSftp: boolean;
    emails: string[];
    report: number[];
    schedule_anchor_date?: string;
    // Per-report configuration keyed by report id (e.g. trades_input /
    // extra_fields for reports 39 & 56, company array for report 44).
    extra_flags?: Record<number, unknown>;
  };
}

export interface CreateDashboardClassClusterReportPayload {
  dashboard_id: string;
  cluster_ids: string[];
  classification_tag_ids: string[];
  report_name: string;
  description?: string;
  layout_config?: Record<string, any>;
  user_id: string;
}

export interface UpdateRegularClusterReportPayload {
  user_id: string;
  data: {
    reportClusterFreqId: number;
    clusterId: number;
    frequency: number;
    isEmail: boolean;
    isSftp: boolean;
    emails: string[];
    report: number[];
    schedule_anchor_date?: string;
    // Per-report configuration keyed by report id (e.g. trades_input /
    // extra_fields for reports 39 & 56, company array for report 44).
    extra_flags?: Record<number, unknown>;
  };
}

export interface UpdateSponsorDashReportPayload extends BaseReportPayload {
  sponsor_id?: string;
  dashboard_config?: Record<string, any>;
  visibility_settings?: {
    is_public: boolean;
    allowed_users?: string[];
    allowed_groups?: string[];
  };
  refresh_interval?: number;
}

export interface DeleteClusterFrequencyReportPayload {
  user_id: string;
  data: {
    reportClusterFreqId: string;
  };
}

// getReportsForClusterData
export interface SavedReportsCluster {
  id: number;
  cluster_name: string;
  is_sftp: number;
  is_email: number;
  calendar_pmca: {
    calendar_pmca_id: number;
    calendar_name: string;
    calendar: number;
  };
  classification_tags: {
    classification_tag_id: string;
    sponsor_id: string | null;
  }[];
}

export interface SavedReportsFrequency {
  id: number;
  report_name: string;
  description: string | null;
}

export interface SavedReportsDefinition {
  report_id: number;
  report_code?: string | null;
  report_name: string;
  report_description: string;
  display_name: string;
}

export interface GetSavedReportsForClusterDataSuccessPayload {
  clusters: SavedReportsCluster[];
  frequencies: SavedReportsFrequency[];
  emails: string[];
  reports: SavedReportsDefinition[];
  monte_carlo_report?: SavedReportsDefinition | null;
}

/* -------------------------------------------------------------------------- */
/*  Scheduled widgets — /v2/report-center/scheduled-widgets/*                 */
/* -------------------------------------------------------------------------- */

// Every scheduled-widget endpoint carries the acting user id.
export interface ScheduledWidgetsBasePayload {
  user_id: string;
}

export interface ScheduledWidgetCalendar {
  calendar_pmca_id: number;
  calendar_name: string;
  calendar?: number;
}

export interface ScheduledWidgetCluster {
  id: number;
  cluster_name: string;
  is_sftp?: 0 | 1;
  is_email?: 0 | 1;
  calendar_pmca?: ScheduledWidgetCalendar | null;
}

export interface ScheduledWidgetFrequency {
  id: number;
  report_name: string;
  description?: string | null;
}

// A widget available for scheduling (reference data).
export interface ScheduledWidgetDefinition {
  id: number;
  widget_name: string;
  display_name: string;
}

export interface GetScheduledWidgetsReferenceDataSuccessPayload {
  clusters: ScheduledWidgetCluster[];
  frequencies: ScheduledWidgetFrequency[];
  widgets: ScheduledWidgetDefinition[];
  emails: string[];
}

// One row of report_widget_bespoke_params (custom timing relative to period end).
export interface ScheduledWidgetBespokeParam {
  id?: number;
  is_active: 0 | 1;
  if_preceding: 0 | 1;
  number_of_days: number | null;
  consider_business_day: 0 | 1;
  week: number | null;
  day_of_week: number | null;
  // Read-only, derived by the backend from the fields above — the weekday the
  // rule actually resolves to and a plain-English summary of it. Not accepted
  // by create/update; only ever displayed.
  effective_day_of_week?: number | null;
  effective_day_of_week_label?: string | null;
  schedule_description?: string | null;
}

// An id inside widget_input paired with the name it refers to, so the grid and
// the edit dialog can show "GBP SONIA" instead of 10.
export interface ScheduledWidgetResolvedRef {
  id: number;
  label: string;
}

// A widget attached to a schedule, as returned by .../get/.
export interface ScheduledWidgetEntry {
  id: number;
  widget_id: number;
  widget_name: string;
  display_name: string;
  // Stored as a JSON string; `widget_input_display` is the parsed version.
  widget_input: string;
  widget_input_display?: Record<string, unknown> | null;
  // Per widget_input key, the resolved names for the ids it holds. Only the
  // keys the backend knows how to resolve are present (e.g. `index`).
  widget_input_resolved?: Record<
    string,
    ScheduledWidgetResolvedRef[] | null
  > | null;
}

export interface ScheduledWidgetRecipient {
  email_id: string;
  is_pmca_user: boolean;
  send_as_bcc: 0 | 1;
}

// A ReportWidgetClusterFrequency row — one grid row of the Scheduled Widgets tab.
export interface ScheduledWidget {
  id: number;
  cluster_id: number;
  report_frequency_id: number;
  is_sftp: 0 | 1;
  is_email: 0 | 1;
  is_bespoke_rpt: 0 | 1;
  cluster_data?: ScheduledWidgetCluster | null;
  frequency?: ScheduledWidgetFrequency | null;
  widgets: ScheduledWidgetEntry[];
  bespoke_params: ScheduledWidgetBespokeParam[];
  // Recipients for this cluster + frequency — shared with the regular Report
  // Center reports on the same pair.
  emails: ScheduledWidgetRecipient[];
}

export interface GetScheduledWidgetsSuccessPayload {
  scheduled_widgets: ScheduledWidget[];
}

// widget_input may be sent as an object — the backend serializes it.
export interface ScheduledWidgetInputPayload {
  widget_id: number;
  widget_input: Record<string, unknown> | string;
}

export interface CreateScheduledWidgetPayload {
  user_id: string;
  data: {
    cluster_id: number;
    frequency_id: number;
    is_email?: 0 | 1;
    is_sftp?: 0 | 1;
    is_bespoke_rpt?: 0 | 1;
    widgets: ScheduledWidgetInputPayload[];
    bespoke_params?: ScheduledWidgetBespokeParam[];
    // Recipients for this cluster + frequency, as plain addresses — the same
    // shape the regular cluster report endpoints take. Full replacement.
    emails?: string[];
  };
}

export interface CreateScheduledWidgetSuccessPayload {
  widget_cluster_frequency_id: number;
}

// `widgets` / `bespoke_params` are full replacements — omit them to leave children untouched.
export interface UpdateScheduledWidgetPayload {
  user_id: string;
  data: {
    widget_cluster_frequency_id: number;
    cluster_id?: number;
    frequency_id?: number;
    is_email?: 0 | 1;
    is_sftp?: 0 | 1;
    is_bespoke_rpt?: 0 | 1;
    widgets?: ScheduledWidgetInputPayload[];
    bespoke_params?: ScheduledWidgetBespokeParam[];
    emails?: string[];
  };
}

export interface DeleteScheduledWidgetPayload {
  user_id: string;
  data: {
    widget_cluster_frequency_id: number;
  };
}

export interface ScheduledWidgetIdPayload {
  widget_cluster_frequency_id: number;
}
